export const translations = {
  en: {
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      retry: 'Retry',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      download: 'Download',
      submit: 'Submit',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      yes: 'Yes',
      no: 'No',
      confirm: 'Confirm',
      of: 'of',
      page: 'Page',
      showing: 'Showing',
      results: 'results',
      noData: 'No data available',
      welcome: 'Welcome',
      loadMore: 'Load More',
      refresh: 'Refresh',
      more: 'more',
      viewAll: 'View All',
      total: 'total',
      details: 'Details',
      status: 'Status',
      notes: 'Notes',
    },

    // Navigation
    nav: {
      dashboard: 'Dashboard',
      contracts: 'Contracts',
      billsPayments: 'Bills & Payments',
      inventory: 'Inventory Details',
      profile: 'Profile',
      correspondence: 'Correspondence',
      logout: 'Logout',
      menu: 'Menu',
      // Inventory Details submenu
      invDashboard: 'Dashboard',
      invOperations: 'Operations',
      invAsn: 'Pre-Arrival ASN',
      invDispatch: 'Dispatch Request GRN',
      invDisputesHeading: 'Disputes',
      invDisputes: 'Disputes',
      invDisputeMandates: 'Dispute Mandates',
      invQaTemplates: 'QA Template',
      invMasterData: 'Master Data',
      invSuppliers: 'Suppliers',
      invProducts: 'Products',
      invCategories: 'Categories',
    },

    // Auth
    auth: {
      login: 'Login',
      logout: 'Logout',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot Password?',
      signUp: 'Sign Up',
      signUpRequest: 'Request Sign Up',
      rememberMe: 'Remember me',
      loginButton: 'Sign In',
      signUpButton: 'Create Account',
      requestSignUp: 'Request Access',
      backToLogin: 'Back to Login',
      wrongCredentials: 'Wrong username or password',
      emailNotFound: 'Email does not exist',
      emailNotRegistered: 'Email not registered in our system',
      resetLinkSent: 'We sent you a reset link',
      signUpLinkSent: 'We sent you a signup link',
      passwordMismatch: 'Passwords do not match',
      signUpSuccess: 'Account created successfully! You can now login.',
      enterEmail: 'Enter your email address',
      enterPassword: 'Enter your password',
      setPassword: 'Set Password',
      welcomeBack: 'Welcome Back',
      signInToContinue: 'Sign in to continue to your dashboard',
      resetPassword: 'Reset Password',
      resetPasswordDesc: 'Enter your email to receive a reset link',
      signUpRequestDesc: 'Enter your Customer ID and email to request access',
      createAccount: 'Create Your Account',
      setYourPassword: 'Set your password to complete registration',
      // New translations for OTP flow
      customerId: 'Customer ID',
      enterCustomerId: 'Enter your Customer ID',
      invalidCredentials: 'Invalid credentials. Please check your email and Customer ID.',
      setNewPassword: 'Set New Password',
      setNewPasswordDesc: 'Create a secure password for your account',
      newPassword: 'New Password',
      enterNewPassword: 'Enter your new password',
      confirmNewPassword: 'Confirm your new password',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters long',
      passwordSetSuccess: 'Password set successfully! Redirecting to login...',
      passwordSetFailed: 'Failed to set password. Please try again.',
      otpExpired: 'Your one-time password has expired. Please request a new one.',
      // Password reset translations
      resetPasswordButton: 'Reset Password',
      resetPasswordNewDesc: 'Enter your new password below',
      passwordResetSuccess: 'Password reset successfully! Redirecting to login...',
      passwordResetFailed: 'Failed to reset password. The link may have expired.',
      invalidResetLink: 'Invalid Reset Link',
      invalidResetLinkDesc: 'This password reset link is invalid or has expired.',
      requestNewResetLink: 'Request a new reset link',
    },

    // Server Error
    serverError: {
      title: 'Server Not Responding',
      description: 'We are unable to connect to the server. Please check your internet connection or try again later.',
      retryButton: 'Try Again',
      checkConnection: 'Check your internet connection',
      contactSupport: 'If the problem persists, contact support',
    },

    noActiveContract: {
      title: 'No active contract',
      description: 'Your account does not have an active rental contract yet. Once your account manager activates one, this portal will unlock automatically.',
      contactSupport: 'Reach out to your account manager or support team.',
      checkAgainHint: 'Once your contract is activated, click "Check again" to enter the portal.',
      checkAgain: 'Check again',
      stillInactive: 'Still no active contract.',
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      overview: 'Overview',
      totalContracts: 'Total Contracts',
      activeContracts: 'Active Contracts',
      pendingPayments: 'Pending Payments',
      totalInventory: 'Total Inventory',
      recentActivity: 'Recent Activity',
      noRecentActivity: 'No recent activity yet',
      activityOrderHint: 'Newest at the bottom · click "Load older" to see history',
      quickActions: 'Quick Actions',
      viewContracts: 'View Contracts',
      makePayment: 'Make Payment',
      viewInventory: 'View Inventory',
      storageUsed: 'Storage Used',
      openInvoices: 'Open Invoices',
      overdueInvoices: 'Overdue Invoices',
      upcomingPayments: 'Upcoming Payments',
      lowStockItems: 'Low Stock Items',
      // Hard rule 8: shown instead of a number when the active contract
      // mixes billing units, so no honest storage limit exists.
      unavailable: 'Unavailable',
    },

    // Contracts
    contracts: {
      title: 'Contracts',
      activeContract: 'Active Contract',
      contractHistory: 'Contract History',
      contractNumber: 'Contract Number',
      startDate: 'Start Date',
      endDate: 'End Date',
      status: 'Status',
      storageUsed: 'Storage Used',
      storageLimit: 'Storage Limit',
      remaining: 'Remaining',
      downloadContract: 'Download Contract',
      active: 'Active',
      expired: 'Expired',
      pending: 'Pending',
      progress: 'Progress',
      of: 'of',
      used: 'used',
      contractTime: 'Contract Time',
      days: 'days',
      elapsed: 'elapsed',
      remainingShort: 'remaining',
      // P0 (D2/B3): shown in place of a number when the source
      // contract's rental lines use mixed billing units or a single
      // non-cbm unit — the backend cannot honestly aggregate a total
      // in that case.
      unavailable: 'Unavailable',
      mixedUnitsWarning: 'Storage limit unavailable — contract uses mixed billing units',
    },

    // Bills & Payments
    bills: {
      title: 'Bills & Payments',
      openInvoices: 'Open & Overdue Invoices',
      upcomingInvoices: 'Upcoming Periodic Invoices',
      paidInvoices: 'Paid Invoices',
      paymentHistory: 'Payment History',
      wallet: 'Wallet',
      duePayments: 'Due & Upcoming Payments',
      invoiceNumber: 'Invoice Number',
      amount: 'Amount',
      dueDate: 'Due Date',
      status: 'Status',
      payAll: 'Pay All',
      pay: 'Pay',
      payNow: 'Pay Now',
      payInAdvance: 'Pay in Advance',
      downloadInvoice: 'Download',
      open: 'Open',
      overdue: 'Overdue',
      paid: 'Paid',
      pending: 'Pending',
      walletBalance: 'Wallet Balance',
      autoDeduction: 'Auto Deduction',
      topUp: 'Top Up Wallet',
      lastPayment: 'Last Payment',
      totalPaid: 'Total Paid',
      filterByDate: 'Filter by Date',
      last6Months: 'Last 6 Months',
      last12Months: 'Last 12 Months',
      allTime: 'All Time',
      customRange: 'Custom Range',
      paymentDate: 'Payment Date',
      paymentMethod: 'Payment Method',
      reference: 'Reference',
    },

    // Payment Modal
    payment: {
      title: 'Make Payment',
      invoiceAmount: 'Invoice Amount',
      paymentAmount: 'Payment Amount',
      partialPayment: 'Partial Payment',
      fullPayment: 'Full Payment',
      selectMethod: 'Select Payment Method',
      debitCard: 'Debit Card',
      creditCard: 'Credit Card',
      applePay: 'Apple Pay',
      googlePay: 'Google Pay',
      cardNumber: 'Card Number',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      cardHolder: 'Card Holder Name',
      processPayment: 'Process Payment',
      paymentSuccess: 'Payment Successful',
      paymentFailed: 'Payment Failed',
      processing: 'Processing...',
    },

    // Inventory
    inventory: {
      title: 'Inventory Details',
      overview: 'Overview',
      movements: 'IN/OUT Movements',
      stockOverview: 'Stock Overview',
      productName: 'Product Name',
      barcode: 'Barcode',
      quantity: 'Quantity',
      date: 'Date',
      type: 'Type',
      incoming: 'Incoming',
      outgoing: 'Outgoing',
      inStock: 'In Stock',
      threshold: 'Threshold',
      setThreshold: 'Set Threshold',
      belowThreshold: 'Below Threshold',
      searchProducts: 'Search by name or barcode',
      movementLog: 'Movement Log',
      currentStock: 'Current Stock',
      alertsEnabled: 'Stock Alerts Enabled',
      // Stats
      totalSkus: 'Total SKUs',
      totalInStock: 'In Stock',
      totalOutOfStock: 'Out of Stock',
      lowStock: 'Low Stock',
      expired: 'Expired',
      totalValue: 'Total Value',
      viewAll: 'View All',
      // Products
      products: 'Products',
      category: 'Category',
      unit: 'Unit',
      expiryDate: 'Expiry Date',
      location: 'Location',
      costPrice: 'Cost Price',
      sellingPrice: 'Selling Price',
      supplier: 'Supplier',
      description: 'Description',
      editProduct: 'Edit Product',
      productDetails: 'Product Details',
      noExpiry: 'No Expiry',
      createProduct: 'Create Product',
      createNewProduct: 'Create New Product',
      newProduct: 'New Product',
      requiredFieldsError: 'Please fill in all required fields: Name, Barcode, and Category',
      nameRequired: 'Supplier name is required',
      autoGeneratedOnSave: 'Auto-generated on save',
      saveError: 'Failed to save. Please try again.',
      savedSuccess: 'Saved successfully!',
      productCreatedAndLinked: 'Product created and linked to supplier!',
      supplierCreatedAndLinked: 'Supplier created and linked to product!',
      statusUpdateFailed: 'Failed to update status. Please try again.',
      basicInfo: 'Basic Information',
      stockAndPricing: 'Stock & Pricing',
      dimensions: 'Dimensions',
      length: 'Length',
      width: 'Width',
      height: 'Height',
      volume: 'Volume',
      locationAndSupplier: 'Location & Supplier',
      quickStats: 'Quick Stats',
      productInfo: 'Product Info',
      pricing: 'Pricing',
      stockValue: 'Stock Value',
      profitMargin: 'Profit Margin',
      saving: 'Saving...',
      save: 'Save',
      edit: 'Edit',
      cancel: 'Cancel',
      back: 'Back',
      productsFound: 'products found',
      noProductsMatch: 'No products match your search/filter',
      selectOrTypeCategory: 'Select or type category...',
      addAsNewCategory: 'Add "{value}" as new category',
      noMatchingCategories: 'No matching categories',
      // Group By
      groupBy: 'Group By',
      noGrouping: 'No Grouping',
      productCategory: 'Product Category',
      allUsers: 'All Users',
      clearFilters: 'Clear Filters',
      // ASN & Receiving
      asn: 'Pre-Arrival ASN',
      receiving: 'Stock Receiving',
      asnFull: 'Advance Shipment Notice',
      createAsn: 'Create ASN',
      asnReference: 'Reference',
      expectedDate: 'Expected Date',
      receivedDate: 'Received Date',
      supplierName: 'Supplier',
      notes: 'Notes',
      expectedQty: 'Expected Qty',
      receivedQty: 'Received Qty',
      asnDetails: 'ASN Details',
      noAsn: 'No ASN records found',
      pendingAsn: 'Pending ASN',
      newAsn: 'New ASN',
      cancelAsn: 'Cancel ASN',
      asnRequiredFieldsError: 'Please fill in Expected Date and add at least one product',
      supplierOptionalHint: 'Supplier is optional. Selecting one filters products to that supplier.',
      asnCancelledDescription: 'This ASN has been cancelled',
      confirmCancel: 'Are you sure you want to cancel this ASN?',
      addProductsFirst: 'Please add at least one product before submitting',
      submittedForReview: 'ASN submitted for review',
      asnApproved: 'ASN approved successfully',
      asnCancelled: 'ASN cancelled successfully',
      statusUpdated: 'Status updated',
      cancelEdit: 'Cancel',
      asnInfo: 'ASN Information',
      reference: 'Reference',
      approvedAt: 'Approved At',
      help: 'Help',
      notesPlaceholder: 'Additional notes...',
      useSupplier: 'Use',
      selectOrTypeSupplier: 'Select or type supplier name',
      noProductsAdded: 'No products added yet',
      selectProduct: 'Select a product',
      selectSupplierFirst: 'Please select a supplier first to add products',
      loadingProducts: 'Loading products...',
      noProductsForSupplier: 'No products found for this supplier',
      noProductsAvailable: 'No products available',
      qty: 'Qty',
      addNote: 'Add note',
      viewNote: 'View note',
      productNote: 'Product Note',
      enterNoteForProduct: 'Enter a note for this product...',
      draftDescription: 'You can edit the ASN and add products.',
      reviewDescription: 'Waiting for administrative approval.',
      approvedDescription: 'ASN has been approved and is ready for receiving.',
      resolvedDescription: 'This request has been resolved automatically when the receiving process is completed.',
      // Stock receiving lifecycle (post-approval, from Odoo)
      receivingJourney: 'Receiving Journey',
      receivingJourneyDescription: 'Real-time progress of your shipment in our warehouse.',
      receivingReference: 'Receiving Reference',
      currentStage: 'Current Stage',
      expectedArrival: 'Expected Arrival',
      arrivedAt: 'Arrived At',
      activityTimeline: 'Activity Timeline',
      workflowTimeline: 'Status Timeline',
      workflowTimelineDescription: 'Track your shipment from draft through stock in.',
      upNext: 'Up next',
      pending: 'Pending',
      createdReceiving: 'Receiving created',
      stateChange: 'Status changed',
      stateWfa: 'Waiting for Arrival',
      stateArrived: 'Shipment Arrived',
      stateCount: 'Counting',
      stateQa: 'Quality Inspection',
      stateDispute: 'Dispute',
      stateOnHold: 'On Hold',
      stateConfirm: 'Confirmed',
      stateEntryDone: 'Stock In',
      stateDraftReceiving: 'Receiving Draft',
      stateWfaDescription: 'Your shipment is registered and we are awaiting its arrival.',
      stateArrivedDescription: 'Shipment has arrived at the warehouse.',
      stateCountDescription: 'Our team is counting the received items.',
      stateQaDescription: 'Items are undergoing quality inspection.',
      stateConfirmDescription: 'Receiving is confirmed; preparing stock entry.',
      stateEntryDoneDescription: 'Items have been added to your inventory.',
      stateDisputeDescription: 'A discrepancy has been raised. We will reach out for resolution.',
      stateOnHoldDescription: 'Receiving is paused pending dispute resolution.',
      noReceivingYet: 'Your receiving document will appear here once the ASN is approved.',
      by: 'by',
      // Dispatch
      dispatch: 'Dispatch Request GRN',
      createDispatch: 'Create Dispatch',
      newDispatch: 'New Dispatch',
      dispatchReference: 'Reference',
      requestedDate: 'Requested Date',
      dispatchedDate: 'Dispatched Date',
      deliveryAddress: 'Delivery Address',
      priority: 'Priority',
      normal: 'Normal',
      urgent: 'Urgent',
      requestedQty: 'Requested Qty',
      dispatchedQty: 'Dispatched Qty',
      dispatchDetails: 'Dispatch Details',
      noDispatch: 'No dispatch requests found',
      pendingDispatch: 'Pending Dispatch',
      // Status workflow
      status: 'Status',
      draft: 'Draft',
      review: 'Under Review',
      approved: 'Approved',
      resolved: 'Resolved',
      readyToReceive: 'Ready to Receive',
      readyToDispatch: 'Ready to Dispatch',
      inProcess: 'In Process',
      done: 'Done',
      submitForReview: 'Submit for Review',
      approve: 'Approve',
      reject: 'Reject',
      markReady: 'Mark Ready',
      startProcess: 'Start Process',
      complete: 'Complete',
      setToDraft: 'Set to Draft',
      cancelled: 'Cancelled',
      cancelledDescription: 'This request has been rejected.',
      readonlyMode: 'Read Only',
      // Form
      selectProducts: 'Select Products',
      selectProductPlaceholder: 'Select a product...',
      addProduct: 'Add Product',
      removeProduct: 'Remove',
      attachments: 'Attachments',
      createdBy: 'Created By',
      approvedBy: 'Approved By',
      createdAt: 'Created At',
      enterSupplierName: 'Enter supplier name',
      enterNotes: 'Enter notes...',
      productsAdded: 'products added',
      addProductsToAsn: 'Add products that you expect to receive',
      summary: 'Summary',
      asnInfoDescription: 'Advance Shipment Notice helps track incoming inventory before it arrives.',
      enterDeliveryAddress: 'Enter delivery address...',
      addProductsToDispatch: 'Add products you want to dispatch',
      dispatchInfo: 'Dispatch Information',
      dispatchInfoDescription: 'Dispatch requests help manage and track outbound shipments.',
      // Charts
      stockDistribution: 'Stock Distribution',
      topProducts: 'Top Products',
      categoryDistribution: 'Category Distribution',
      recentMovements: 'Recent Movements',
      inventoryValue: 'Inventory Value',
      // Filters
      filterByStatus: 'Filter by Status',
      filterByCategory: 'Filter by Category',
      allStatuses: 'All Statuses',
      allCategories: 'All Categories',
      all: 'All',
      outOfStock: 'Out of Stock',
      // Overview page
      overviewDescription: 'Comprehensive overview of your warehouse inventory',
      hiddenUntrackedStock: 'Some items are not displayed because they are not batch/lot tracked. Please contact us to reconcile them.',
      productsSKUs: 'Products / SKUs',
      totalInventoryValue: 'Total inventory value',
      categories: 'Categories',
      totalSKUs: 'total SKUs',
      // Overview sections
      operationsTitle: 'Operations',
      operationsDescription: 'Track incoming and outgoing inventory',
      masterDataTitle: 'Master Data',
      masterDataDescription: 'Products and configuration',
      // Dispute Mandates page
      disputeMandatesTitle: 'Dispute Mandates',
      disputeMandatesDescription: 'Pre-agreed rules that govern how your inbound disputes are auto-resolved',
      mandatesEmpty: 'No mandates yet. Create one to define how the warehouse should auto-resolve your inbound disputes.',
      createMandate: 'New Mandate',
      editMandate: 'Edit Mandate',
      mandateReference: 'Reference',
      mandateContract: 'Contract / SLA',
      mandateNoContract: 'No specific contract',
      mandateEffective: 'Effective Date',
      mandateExpiry: 'Expiry Date',
      mandateExpiryHint: 'Leave empty for no expiration',
      mandateValidity: 'Validity',
      mandateValid: 'Valid',
      mandateInvalid: 'Not valid',
      mandateSignatory: 'Authorized Signatory',
      mandateSignatoryTitle: 'Signatory Title',
      mandateSignatoryEmail: 'Signatory Email',
      mandateSlaHours: 'Client Response SLA (hours)',
      mandateDefaultAction: 'Default Action (no response)',
      mandateShipmentThreshold: 'Shipment Value Threshold',
      mandateSkuThreshold: 'SKU Value Threshold',
      mandateThresholdHelp: '0 = no limit',
      mandateNotes: 'Notes',
      mandateRules: 'Mandate Rules',
      mandateRulesEmpty: 'No rules yet. Use "Add default rules" to seed all dispute types, or add rules manually below.',
      mandateAddRule: 'Add Rule',
      mandateAddDefaultRules: 'Add Default Rules',
      mandateRuleDisputeType: 'Dispute Type',
      mandateRuleGranted: 'Granted',
      mandateRuleThresholdType: 'Threshold Type',
      mandateRuleThresholdValue: 'Threshold Value',
      mandateRuleAction: 'Allowed Action',
      mandateAuthorizationSection: 'Authorization',
      mandateValiditySection: 'Validity',
      mandateThresholdSection: 'Thresholds & SLA',
      mandateBackToList: 'Back to Mandates',
      mandateSubmitForApproval: 'Submit for Approval',
      mandateCancel: 'Cancel Mandate',
      mandateResetToDraft: 'Reset to Draft',
      mandateSaved: 'Mandate saved',
      mandateCreated: 'Mandate created',
      mandateSaveFailed: 'Failed to save mandate',
      mandateLoadFailed: 'Failed to load mandate',
      mandateSubmittedForApproval: 'Mandate submitted for approval',
      mandateCancelled: 'Mandate cancelled',
      mandateResetDone: 'Mandate reset to draft',
      mandateConfirmSubmit: 'Submit this mandate for approval? You will not be able to edit rules until it is approved or reset.',
      mandateConfirmCancel: 'Cancel this mandate? Approved mandates cannot be deleted.',
      mandateStateDraft: 'Draft',
      mandateStatePendingApproval: 'Pending Approval',
      mandateStateApproved: 'Approved',
      mandateStateExpired: 'Expired',
      mandateStateCancelled: 'Cancelled',
      mandateRequiredSignatory: 'Authorized Signatory is required',
      mandateRequiredEffective: 'Effective Date is required',
      mandateRuleAtLeastOne: 'Add at least one rule before submitting',
      // QA Template page
      qaTemplatesTitle: 'QA Template',
      qaTemplatesDescription: 'Quality-inspection templates applied during inbound receiving',
      qaTemplatesEmpty: 'No QA templates yet. Create one to define which checks should run on your inbound receivings.',
      createQaTemplate: 'New Template',
      editQaTemplate: 'Edit Template',
      qaTemplateName: 'Template Name',
      qaTemplateSequence: 'Sequence',
      qaTemplateActive: 'Active',
      qaTemplateScope: 'Scope',
      qaTemplateGlobalDefault: 'Global default',
      qaTemplateGlobal: 'Global',
      qaTemplateCategory: 'Product Category',
      qaTemplateNoCategory: 'All categories',
      qaTemplateNotes: 'Notes',
      qaTemplateChecks: 'Checks',
      qaTemplateChecksEmpty: 'No checks yet. Click "Add check" to add an inspection step.',
      qaTemplateAddCheck: 'Add Check',
      qaTemplateCheckType: 'Check Type',
      qaTemplateSamplingMethod: 'Sampling Method',
      qaTemplateSampleSize: 'Sample Size',
      qaTemplateMandatory: 'Mandatory',
      qaTemplateInstructions: 'Instructions',
      qaTemplateBackToList: 'Back to Templates',
      qaTemplateOwnedByYou: 'Owned by you',
      qaTemplateReadOnly: 'Read-only (global template — clone to customise)',
      qaTemplateCloneToCustomise: 'Clone to customise',
      qaTemplateSaved: 'Template saved',
      qaTemplateCreated: 'Template created',
      qaTemplateDeleted: 'Template deleted',
      qaTemplateSaveFailed: 'Failed to save template',
      qaTemplateLoadFailed: 'Failed to load template',
      qaTemplateDeleteFailed: 'Failed to delete template',
      qaTemplateConfirmDelete: 'Delete this template? This cannot be undone.',
      qaTemplateRequiredName: 'Template name is required',
      qaTemplateRequiredCheck: 'Add at least one check',
      // Dispute detail page
      disputeBackToList: 'Back to Disputes',
      disputeDetailTitle: 'Dispute',
      disputeLoadFailed: 'Failed to load dispute',
      disputeApprove: 'Approve Resolution',
      disputeReject: 'Reject Resolution',
      disputeClientApprove: 'Approve',
      disputeClientReject: 'Reject',
      disputeApproveTitle: 'Approve resolution',
      disputeApproveMessage: 'Confirm the action the warehouse should take to resolve this dispute.',
      disputeRejectTitle: 'Reject resolution',
      disputeRejectMessage: 'Tell the warehouse why you disagree with the proposed resolution. The dispute will be moved to Rejected.',
      disputeApproved: 'Resolution approved',
      disputeRejected: 'Resolution rejected',
      disputeActionFailed: 'Action failed',
      disputeResolution: 'Resolution',
      disputeSuggestedAction: 'Suggested Action',
      disputeResolutionAction: 'Resolution Action',
      disputeClientNotes: 'Your Notes (optional)',
      disputeMandateUsed: 'Applicable Mandate',
      disputeMandateEvaluation: 'Mandate Evaluation',
      disputeAttachments: 'Evidence',
      disputeNoAttachments: 'No attachments',
      disputeDescription: 'Description',
      disputeQuantities: 'Quantities',
      disputeAsnQty: 'ASN Qty',
      disputeActualQty: 'Actual Qty',
      disputeVarianceQty: 'Variance',
      disputeUnitValue: 'Unit Value',
      disputeTotalValue: 'Disputed Value',
      disputeWarehouseResolved: 'Warehouse has resolved this dispute',
      disputeClosedInfo: 'Dispute closed and locked',
      disputeAwaitingClient: 'Awaiting your decision',
      disputeNoActionsAvailable: 'No actions available — this dispute is already resolved.',
      comingSoon: 'Coming Soon',
      comingSoonDescription: 'This page will be available shortly. Reach out to your account manager for early access.',
      // Disputes page
      disputesTitle: 'Disputes',
      disputesDescription: 'Inbound receiving disputes raised against your shipments',
      disputesEmpty: 'No disputes match the current filters',
      disputesFilterAll: 'All',
      disputesFilterSlaBreach: 'SLA Breach',
      disputesFilterSlaBreachActive: 'SLA Breach only',
      disputeReference: 'Reference',
      disputeType: 'Type',
      disputeSeverity: 'Severity',
      disputeStatus: 'Status',
      disputeProduct: 'Product',
      disputeAsn: 'ASN',
      disputeReceiving: 'Receiving',
      disputeVariance: 'Variance',
      disputeValue: 'Disputed Value',
      disputeSla: 'SLA',
      disputeSlaDeadline: 'SLA Deadline',
      disputeCreated: 'Created',
      disputeNotified: 'Notified',
      disputeResponded: 'Responded',
      // Dispute state labels
      disputeStateDraft: 'Draft',
      disputeStateOpen: 'Open',
      disputeStateWaitingClient: 'Waiting for Client',
      disputeStateAutoResolved: 'Auto-Resolved',
      disputeStateClientApproved: 'Client Approved',
      disputeStateRejected: 'Rejected',
      disputeStateClosed: 'Closed',
      // Severity labels
      disputeSeverityMinor: 'Minor',
      disputeSeverityMajor: 'Major',
      disputeSeverityCritical: 'Critical',
      // SLA labels
      slaOnTrack: 'On Track',
      slaWarning: 'Warning',
      slaBreached: 'Breached',
      slaNA: 'N/A',
      // Status labels (for filters/badges) - Stock only
      statusInStock: 'In Stock',
      statusOutOfStock: 'Out of Stock',
      statusLowStock: 'Low Stock',
      // Image Upload
      clickToUpload: 'Click to upload',
      dragAndDrop: 'drag and drop',
      or: 'or',
      upTo: 'up to',
      uploading: 'Uploading...',
      changeImage: 'Change Image',
      removeImage: 'Remove Image',
      invalidImageFormat: 'Please select a valid image file (PNG, JPG, GIF)',
      imageTooLarge: 'Image size should be less than 5MB',
      uploadFailed: 'Failed to upload image',
      productImage: 'Product Image',
      // Supplier Combobox
      noMatchingSuppliers: 'No matching suppliers',
      addNewSupplier: 'Add new supplier',
      addAsNewSupplier: 'Add "{supplier}" as new supplier',
      // Table headers
      actions: 'Actions',
      expiry: 'Expiry',
      // Summary metrics
      totalSuppliers: 'Total Suppliers',
      totalCategories: 'Total Categories',
      totalUnits: 'Total Units',
      totalQuantityOnHand: 'Total Quantity on Hand',
      qtyOnHand: 'Qty on Hand',
      viewProducts: 'View Products',
      filteringBy: 'Filtering by',
      clearContext: 'Clear Filter',
      // Product Tabs
      warehousesLocations: 'Warehouses & Locations',
      suppliers: 'Suppliers',
      batches: 'Batches / Lots',
      // Management Pages
      manageSuppliers: 'Manage suppliers',
      categoriesTitle: 'Categories',
      manageCategories: 'Manage categories',
      unitsTitle: 'Units',
      manageUnits: 'Manage units',
      createSupplier: 'Create Supplier',
      newSupplier: 'New Supplier',
      editSupplier: 'Edit Supplier',
      supplierDetails: 'Supplier Details',
      noSuppliersFound: 'No suppliers found',
      productsSupplied: 'Products Supplied',
      noProductsSupplied: 'No products supplied by this supplier yet',
      viewAllProducts: 'View All Products',
      viewInProductList: 'View in Product List',
      totalQuantityProvided: 'Total Qty Provided',
      qtyProvided: 'Qty Provided',
      preferred: 'Preferred',
      lastOrder: 'Last Order',
      days: 'days',
      view: 'View',
      totalProducts: 'Total Products',
      totalQuantity: 'Total Quantity',
      product: 'Product',
      availableQuantity: 'Available Quantity',
      reservedQuantity: 'Reserved Quantity',
      suppliersOfThisProduct: 'Suppliers who provide this product',
      addFirstSupplier: 'Add First Supplier',
      viewSupplier: 'View Supplier',
      quickActions: 'Quick Actions',
      createCategory: 'Create Category',
      newCategory: 'New Category',
      editCategory: 'Edit Category',
      categoryDetails: 'Category Details',
      categoryName: 'Category Name',
      noCategoriesFound: 'No categories found',
      createUnit: 'Create Unit',
      newUnit: 'New Unit',
      editUnit: 'Edit Unit',
      unitDetails: 'Unit Details',
      noUnitsFound: 'No units found',
      contactInfo: 'Contact Information',
      address: 'Address',
      website: 'Website',
      activeProducts: 'Active Products',
      totalOrders: 'Total Orders',
      productsCount: 'Products',
      parentCategory: 'Parent Category',
      noParentCategory: 'No Parent (Root Category)',
      active: 'Active',
      inactive: 'Inactive',
      searchSuppliers: 'Search suppliers...',
      searchCategories: 'Search categories...',
      searchUnits: 'Search units...',
      code: 'Code',
      units: 'units',
      conversionSettings: 'Conversion Settings',
      // Warehouse & Location
      warehouse: 'Warehouse',
      selectWarehouse: 'Select Warehouse',
      locationInWarehouse: 'Location',
      selectLocation: 'Select Location',
      addWarehouseLocation: 'Add Warehouse Location',
      noWarehouseLocations: 'No warehouse locations added',
      removeLocation: 'Remove Location',
      // Suppliers (additional fields)
      selectSupplier: 'Select Supplier',
      supplierCode: 'Supplier Code',
      contactPerson: 'Contact Person',
      email: 'Email',
      phone: 'Phone',
      leadTime: 'Lead Time (days)',
      minOrderQty: 'Min Order Qty',
      price: 'Price',
      currency: 'Currency',
      isPreferred: 'Preferred Supplier',
      lastPurchaseDate: 'Last Purchase',
      addSupplier: 'Add Supplier',
      noSuppliers: 'No suppliers added',
      noClientsSuppliers: 'No suppliers assigned to this product',
      removeSupplier: 'Remove Supplier',
      assignedSuppliers: 'Assigned Suppliers',
      supplierAdded: 'Supplier added successfully',
      supplierRemoved: 'Supplier removed successfully',
      errorAddingSupplier: 'Failed to add supplier',
      errorRemovingSupplier: 'Failed to remove supplier',
      vendorPricelist: 'Vendor Pricelist',
      // Products in Supplier
      assignedProducts: 'Assigned Products',
      noProductsAssigned: 'No products assigned to this supplier',
      productAdded: 'Product added successfully',
      productRemoved: 'Product removed successfully',
      errorAddingProduct: 'Failed to add product',
      errorRemovingProduct: 'Failed to remove product',
      // Batches / Lots
      batchNumber: 'Batch Number',
      lotNumber: 'Lot Number',
      manufacturingDate: 'Manufacturing Date',
      batchStatus: 'Batch Status',
      available: 'Available',
      reserved: 'Reserved',
      quarantine: 'Quarantine',
      nearExpiry: 'Near expiry',
      expiryUnknown: 'Unknown',
      addBatch: 'Add Batch',
      noBatches: 'No batches added',
      removeBatch: 'Remove Batch',
      // Unit of Measure
      selectOrTypeUnit: 'Select or type unit...',
      noMatchingUnits: 'No matching units',
      createNewUnit: 'Create New Unit',
      unitCategory: 'Unit Category',
      unitName: 'Unit Name',
      unitSymbol: 'Symbol',
      parentUnit: 'Parent Unit',
      noParentUnit: 'None (Base Unit)',
      parentUnitHelp: 'Select a parent unit to define conversion relationship',
      conversionRatio: 'Conversion Ratio',
      thisUnit: 'this unit',
      conversionExample: '{count} {unit} equals 1 {parent}',
      // Threshold modal
      thresholdAlertMessage: 'You will receive alerts when stock falls below this threshold.',
      // Common actions (use common.cancel, common.close, etc. instead)
      saveChanges: 'Save Changes',
      openFullPage: 'Open Full Page',
      expand: 'Expand',
      close: 'Close',
      add: 'Add',
      noProducts: 'No products found',
      completed: 'Completed',
    },

    // Profile
    profile: {
      title: 'Profile',
      personalInfo: 'Personal Information',
      companyInfo: 'Company Information',
      addresses: 'Addresses',
      name: 'Name',
      email: 'Email',
      companyName: 'Company Name',
      vatNumber: 'VAT Number',
      profileImage: 'Profile Image',
      changeImage: 'Change Image',
      addAddress: 'Add Address',
      addressType: 'Address Type',
      delivery: 'Delivery',
      office: 'Office',
      setDefault: 'Set as Default',
      defaultAddress: 'Default Address',
      addressText: 'Address',
      editAddress: 'Edit Address',
      deleteAddress: 'Delete Address',
      saveChanges: 'Save Changes',
      savedSuccess: 'Profile saved successfully!',
      saveError: 'Failed to save profile',
      addressUpdated: 'Address updated successfully!',
      addressAdded: 'Address added successfully!',
      addressError: 'Failed to save address',
    },

    // Correspondence
    correspondence: {
      title: 'Correspondence',
      emailNotifications: 'Email Notifications',
      subject: 'Subject',
      date: 'Date',
      from: 'From',
      to: 'To',
      preview: 'Preview',
      viewDetails: 'View Details',
      noMessages: 'No messages found',
      compose: 'Compose',
      composeNew: 'New Message',
      body: 'Message',
      attachments: 'Attachments',
      addAttachment: 'Add Attachment',
      removeAttachment: 'Remove',
      send: 'Send',
      sending: 'Sending...',
      sent: 'Sent',
      received: 'Received',
      messageSent: 'Message sent successfully',
      unread: 'unread',
      unreadMessages: 'Unread Messages',
      showingPerPage: 'Showing {count} emails per page',
      filterAll: 'All',
      filterUnread: 'Unread',
      filterWithAttachments: 'With Attachments',
      filterToday: 'Today',
      filterThisWeek: 'This Week',
      filterThisMonth: 'This Month',
      filterCustomRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      applyFilter: 'Apply',
      clearFilter: 'Clear',
      downloadAttachment: 'Download',
      attachmentSize: 'Size',
    },

    // Theme & Language
    settings: {
      theme: 'Theme',
      language: 'Language',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      english: 'English',
      arabic: 'العربية',
    },
  },

  ar: {
    // Common
    common: {
      loading: 'جاري التحميل...',
      error: 'حدث خطأ',
      retry: 'إعادة المحاولة',
      save: 'حفظ',
      cancel: 'إلغاء',
      delete: 'حذف',
      edit: 'تعديل',
      add: 'إضافة',
      search: 'بحث',
      filter: 'تصفية',
      download: 'تحميل',
      submit: 'إرسال',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      close: 'إغلاق',
      yes: 'نعم',
      no: 'لا',
      confirm: 'تأكيد',
      of: 'من',
      page: 'صفحة',
      showing: 'عرض',
      results: 'نتائج',
      noData: 'لا توجد بيانات',
      welcome: 'مرحباً',
      loadMore: 'تحميل المزيد',
      refresh: 'تحديث',
      more: 'المزيد',
      viewAll: 'عرض الكل',
      total: 'إجمالي',
      details: 'التفاصيل',
      status: 'الحالة',
      notes: 'ملاحظات',
    },

    // Navigation
    nav: {
      dashboard: 'لوحة التحكم',
      contracts: 'العقود',
      billsPayments: 'الفواتير والمدفوعات',
      inventory: 'تفاصيل المخزون',
      profile: 'الملف الشخصي',
      correspondence: 'المراسلات',
      logout: 'تسجيل الخروج',
      menu: 'القائمة',
      // قائمة فرعية لتفاصيل المخزون
      invDashboard: 'لوحة التحكم',
      invOperations: 'العمليات',
      invAsn: 'إشعار الشحن المسبق',
      invDispatch: 'طلبات الإرسال (GRN)',
      invDisputesHeading: 'النزاعات',
      invDisputes: 'النزاعات',
      invDisputeMandates: 'تفويضات النزاعات',
      invQaTemplates: 'قوالب الجودة',
      invMasterData: 'البيانات الأساسية',
      invSuppliers: 'الموردون',
      invProducts: 'المنتجات',
      invCategories: 'الفئات',
    },

    // Auth
    auth: {
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      confirmPassword: 'تأكيد كلمة المرور',
      forgotPassword: 'نسيت كلمة المرور؟',
      signUp: 'إنشاء حساب',
      signUpRequest: 'طلب التسجيل',
      rememberMe: 'تذكرني',
      loginButton: 'تسجيل الدخول',
      signUpButton: 'إنشاء الحساب',
      requestSignUp: 'طلب الوصول',
      backToLogin: 'العودة لتسجيل الدخول',
      wrongCredentials: 'اسم المستخدم أو كلمة المرور غير صحيحة',
      emailNotFound: 'البريد الإلكتروني غير موجود',
      emailNotRegistered: 'البريد الإلكتروني غير مسجل في نظامنا',
      resetLinkSent: 'تم إرسال رابط إعادة التعيين',
      signUpLinkSent: 'تم إرسال رابط التسجيل',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      signUpSuccess: 'تم إنشاء الحساب بنجاح! يمكنك الآن تسجيل الدخول.',
      enterEmail: 'أدخل بريدك الإلكتروني',
      enterPassword: 'أدخل كلمة المرور',
      setPassword: 'تعيين كلمة المرور',
      welcomeBack: 'مرحباً بعودتك',
      signInToContinue: 'سجل الدخول للمتابعة إلى لوحة التحكم',
      resetPassword: 'إعادة تعيين كلمة المرور',
      resetPasswordDesc: 'أدخل بريدك الإلكتروني لتلقي رابط إعادة التعيين',
      signUpRequestDesc: 'أدخل رقم العميل والبريد الإلكتروني لطلب الوصول',
      createAccount: 'إنشاء حسابك',
      setYourPassword: 'عيّن كلمة المرور لإكمال التسجيل',
      // New translations for OTP flow
      customerId: 'رقم العميل',
      enterCustomerId: 'أدخل رقم العميل',
      invalidCredentials: 'بيانات غير صالحة. يرجى التحقق من البريد الإلكتروني ورقم العميل.',
      setNewPassword: 'تعيين كلمة مرور جديدة',
      setNewPasswordDesc: 'أنشئ كلمة مرور آمنة لحسابك',
      newPassword: 'كلمة المرور الجديدة',
      enterNewPassword: 'أدخل كلمة المرور الجديدة',
      confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
      passwordsDoNotMatch: 'كلمات المرور غير متطابقة',
      passwordTooShort: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
      passwordSetSuccess: 'تم تعيين كلمة المرور بنجاح! جاري التحويل لتسجيل الدخول...',
      passwordSetFailed: 'فشل في تعيين كلمة المرور. يرجى المحاولة مرة أخرى.',
      otpExpired: 'انتهت صلاحية كلمة المرور المؤقتة. يرجى طلب كلمة مرور جديدة.',
      // Password reset translations
      resetPasswordButton: 'إعادة تعيين كلمة المرور',
      resetPasswordNewDesc: 'أدخل كلمة المرور الجديدة أدناه',
      passwordResetSuccess: 'تم إعادة تعيين كلمة المرور بنجاح! جاري التحويل لتسجيل الدخول...',
      passwordResetFailed: 'فشل في إعادة تعيين كلمة المرور. قد يكون الرابط منتهي الصلاحية.',
      invalidResetLink: 'رابط إعادة التعيين غير صالح',
      invalidResetLinkDesc: 'رابط إعادة تعيين كلمة المرور هذا غير صالح أو منتهي الصلاحية.',
      requestNewResetLink: 'طلب رابط إعادة تعيين جديد',
    },

    // Server Error
    serverError: {
      title: 'الخادم لا يستجيب',
      description: 'لا يمكننا الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت أو المحاولة مرة أخرى لاحقاً.',
      retryButton: 'حاول مرة أخرى',
      checkConnection: 'تحقق من اتصالك بالإنترنت',
      contactSupport: 'إذا استمرت المشكلة، اتصل بالدعم',
    },

    noActiveContract: {
      title: 'لا يوجد عقد نشط',
      description: 'حسابك لا يحتوي على عقد إيجار نشط بعد. بمجرد قيام مدير حسابك بتفعيل العقد، سيتم فتح هذه البوابة تلقائيًا.',
      contactSupport: 'تواصل مع مدير حسابك أو فريق الدعم.',
      checkAgainHint: 'بمجرد تفعيل العقد، اضغط "تحقق مرة أخرى" للدخول إلى البوابة.',
      checkAgain: 'تحقق مرة أخرى',
      stillInactive: 'لا يزال لا يوجد عقد نشط.',
    },

    // Dashboard
    dashboard: {
      title: 'لوحة التحكم',
      welcome: 'مرحباً بعودتك',
      overview: 'نظرة عامة',
      totalContracts: 'إجمالي العقود',
      activeContracts: 'العقود النشطة',
      pendingPayments: 'المدفوعات المعلقة',
      totalInventory: 'إجمالي المخزون',
      recentActivity: 'النشاط الأخير',
      noRecentActivity: 'لا يوجد نشاط حديث',
      activityOrderHint: 'الأحدث في الأسفل · اضغط "تحميل الأقدم" لرؤية السجل',
      quickActions: 'إجراءات سريعة',
      viewContracts: 'عرض العقود',
      makePayment: 'إجراء دفعة',
      viewInventory: 'عرض المخزون',
      storageUsed: 'التخزين المستخدم',
      openInvoices: 'الفواتير المفتوحة',
      overdueInvoices: 'الفواتير المتأخرة',
      upcomingPayments: 'المدفوعات القادمة',
      lowStockItems: 'المنتجات منخفضة المخزون',
      unavailable: 'غير متاح',
    },

    // Contracts
    contracts: {
      title: 'العقود',
      activeContract: 'العقد النشط',
      contractHistory: 'سجل العقود',
      contractNumber: 'رقم العقد',
      startDate: 'تاريخ البدء',
      endDate: 'تاريخ الانتهاء',
      status: 'الحالة',
      storageUsed: 'التخزين المستخدم',
      storageLimit: 'حد التخزين',
      remaining: 'المتبقي',
      downloadContract: 'تحميل العقد',
      active: 'نشط',
      expired: 'منتهي',
      pending: 'معلق',
      progress: 'التقدم',
      of: 'من',
      used: 'مستخدم',
      contractTime: 'مدة العقد',
      days: 'يوم',
      elapsed: 'منقضي',
      remainingShort: 'متبقي',
      // P0 mirror strings — placeholders for UX-team approval.
      unavailable: 'غير متاح',
      mixedUnitsWarning: 'حد التخزين غير متاح — يستخدم العقد وحدات فوترة مختلطة',
    },

    // Bills & Payments
    bills: {
      title: 'الفواتير والمدفوعات',
      openInvoices: 'الفواتير المفتوحة والمتأخرة',
      upcomingInvoices: 'الفواتير الدورية القادمة',
      paidInvoices: 'الفواتير المدفوعة',
      paymentHistory: 'سجل المدفوعات',
      wallet: 'المحفظة',
      duePayments: 'المدفوعات المستحقة والقادمة',
      invoiceNumber: 'رقم الفاتورة',
      amount: 'المبلغ',
      dueDate: 'تاريخ الاستحقاق',
      status: 'الحالة',
      payAll: 'دفع الكل',
      pay: 'دفع',
      payNow: 'ادفع الآن',
      payInAdvance: 'دفع مقدم',
      downloadInvoice: 'تحميل',
      open: 'مفتوح',
      overdue: 'متأخر',
      paid: 'مدفوع',
      pending: 'معلق',
      walletBalance: 'رصيد المحفظة',
      autoDeduction: 'الخصم التلقائي',
      topUp: 'شحن المحفظة',
      lastPayment: 'آخر دفعة',
      totalPaid: 'إجمالي المدفوع',
      filterByDate: 'تصفية حسب التاريخ',
      last6Months: 'آخر 6 أشهر',
      last12Months: 'آخر 12 شهر',
      allTime: 'كل الوقت',
      customRange: 'نطاق مخصص',
      paymentDate: 'تاريخ الدفع',
      paymentMethod: 'طريقة الدفع',
      reference: 'المرجع',
    },

    // Payment Modal
    payment: {
      title: 'إجراء دفعة',
      invoiceAmount: 'مبلغ الفاتورة',
      paymentAmount: 'مبلغ الدفع',
      partialPayment: 'دفع جزئي',
      fullPayment: 'دفع كامل',
      selectMethod: 'اختر طريقة الدفع',
      debitCard: 'بطاقة الخصم',
      creditCard: 'بطاقة الائتمان',
      applePay: 'Apple Pay',
      googlePay: 'Google Pay',
      cardNumber: 'رقم البطاقة',
      expiryDate: 'تاريخ الانتهاء',
      cvv: 'CVV',
      cardHolder: 'اسم حامل البطاقة',
      processPayment: 'معالجة الدفع',
      paymentSuccess: 'تم الدفع بنجاح',
      paymentFailed: 'فشل الدفع',
      processing: 'جاري المعالجة...',
    },

    // Inventory
    inventory: {
      title: 'تفاصيل المخزون',
      overview: 'نظرة عامة',
      movements: 'حركات الدخول/الخروج',
      stockOverview: 'نظرة عامة على المخزون',
      productName: 'اسم المنتج',
      barcode: 'الباركود',
      quantity: 'الكمية',
      date: 'التاريخ',
      type: 'النوع',
      incoming: 'وارد',
      outgoing: 'صادر',
      inStock: 'في المخزون',
      threshold: 'الحد الأدنى',
      setThreshold: 'تعيين الحد الأدنى',
      belowThreshold: 'أقل من الحد الأدنى',
      searchProducts: 'البحث بالاسم أو الباركود',
      movementLog: 'سجل الحركات',
      currentStock: 'المخزون الحالي',
      alertsEnabled: 'تنبيهات المخزون مفعلة',
      // Stats
      totalSkus: 'إجمالي المنتجات',
      totalInStock: 'في المخزون',
      totalOutOfStock: 'نفاد المخزون',
      lowStock: 'مخزون منخفض',
      expired: 'منتهي الصلاحية',
      totalValue: 'القيمة الإجمالية',
      viewAll: 'عرض الكل',
      // Products
      products: 'المنتجات',
      category: 'الفئة',
      unit: 'الوحدة',
      expiryDate: 'تاريخ الانتهاء',
      location: 'الموقع',
      costPrice: 'سعر التكلفة',
      sellingPrice: 'سعر البيع',
      supplier: 'المورد',
      description: 'الوصف',
      editProduct: 'تعديل المنتج',
      productDetails: 'تفاصيل المنتج',
      noExpiry: 'لا يوجد تاريخ انتهاء',
      createProduct: 'إنشاء منتج',
      createNewProduct: 'إنشاء منتج جديد',
      newProduct: 'منتج جديد',
      requiredFieldsError: 'يرجى ملء جميع الحقول المطلوبة: الاسم، الباركود، الوحدة، والفئة',
      nameRequired: 'اسم المورد مطلوب',
      autoGeneratedOnSave: 'يتم إنشاؤه تلقائياً عند الحفظ',
      saveError: 'فشل في الحفظ. يرجى المحاولة مرة أخرى.',
      savedSuccess: 'تم الحفظ بنجاح!',
      productCreatedAndLinked: 'تم إنشاء المنتج وربطه بالمورد!',
      supplierCreatedAndLinked: 'تم إنشاء المورد وربطه بالمنتج!',
      statusUpdateFailed: 'فشل في تحديث الحالة. يرجى المحاولة مرة أخرى.',
      basicInfo: 'المعلومات الأساسية',
      stockAndPricing: 'المخزون والتسعير',
      dimensions: 'الأبعاد',
      length: 'الطول',
      width: 'العرض',
      height: 'الارتفاع',
      volume: 'الحجم',
      locationAndSupplier: 'الموقع والمورد',
      quickStats: 'إحصائيات سريعة',
      productInfo: 'معلومات المنتج',
      pricing: 'التسعير',
      stockValue: 'قيمة المخزون',
      profitMargin: 'هامش الربح',
      saving: 'جاري الحفظ...',
      save: 'حفظ',
      edit: 'تعديل',
      cancel: 'إلغاء',
      back: 'رجوع',
      productsFound: 'منتج تم العثور عليه',
      noProductsMatch: 'لا توجد منتجات تطابق البحث/التصفية',
      selectOrTypeCategory: 'اختر أو اكتب الفئة...',
      addAsNewCategory: 'إضافة "{value}" كفئة جديدة',
      noMatchingCategories: 'لا توجد فئات مطابقة',
      // Group By
      groupBy: 'تجميع حسب',
      noGrouping: 'بدون تجميع',
      productCategory: 'فئة المنتج',
      allUsers: 'جميع المستخدمين',
      clearFilters: 'مسح التصفية',
      // ASN & Receiving
      asn: 'إشعار الشحن المسبق',
      receiving: 'استلام المخزون',
      asnFull: 'إشعار الشحن المتقدم',
      createAsn: 'إنشاء إشعار شحن',
      asnReference: 'المرجع',
      expectedDate: 'التاريخ المتوقع',
      receivedDate: 'تاريخ الاستلام',
      supplierName: 'المورد',
      notes: 'ملاحظات',
      expectedQty: 'الكمية المتوقعة',
      receivedQty: 'الكمية المستلمة',
      asnDetails: 'تفاصيل إشعار الشحن',
      noAsn: 'لا توجد سجلات إشعارات شحن',
      pendingAsn: 'إشعارات شحن معلقة',
      newAsn: 'إشعار شحن جديد',
      cancelAsn: 'إلغاء الإشعار',
      asnRequiredFieldsError: 'يرجى ملء تاريخ التوقع وإضافة منتج واحد على الأقل',
      supplierOptionalHint: 'المورد اختياري. اختيار مورد يقيّد المنتجات بهذا المورد فقط.',
      asnCancelledDescription: 'تم إلغاء هذا الإشعار',
      confirmCancel: 'هل أنت متأكد من إلغاء هذا الإشعار؟',
      addProductsFirst: 'يرجى إضافة منتج واحد على الأقل قبل الإرسال',
      submittedForReview: 'تم إرسال الإشعار للمراجعة',
      asnApproved: 'تمت الموافقة على الإشعار بنجاح',
      asnCancelled: 'تم إلغاء الإشعار بنجاح',
      statusUpdated: 'تم تحديث الحالة',
      cancelEdit: 'إلغاء',
      asnInfo: 'معلومات الإشعار',
      reference: 'المرجع',
      approvedAt: 'تمت الموافقة في',
      help: 'مساعدة',
      notesPlaceholder: 'ملاحظات إضافية...',
      useSupplier: 'استخدام',
      selectOrTypeSupplier: 'اختر أو اكتب اسم المورد',
      noProductsAdded: 'لم يتم إضافة منتجات بعد',
      selectProduct: 'اختر منتج',
      selectSupplierFirst: 'يرجى اختيار مورد أولاً لإضافة المنتجات',
      loadingProducts: 'جاري تحميل المنتجات...',
      noProductsForSupplier: 'لم يتم العثور على منتجات لهذا المورد',
      noProductsAvailable: 'لا توجد منتجات متاحة',
      qty: 'الكمية',
      addNote: 'إضافة ملاحظة',
      viewNote: 'عرض الملاحظة',
      productNote: 'ملاحظة المنتج',
      enterNoteForProduct: 'أدخل ملاحظة لهذا المنتج...',
      draftDescription: 'يمكنك تعديل الإشعار وإضافة المنتجات.',
      reviewDescription: 'في انتظار الموافقة الإدارية.',
      approvedDescription: 'تمت الموافقة على الإشعار وهو جاهز للاستلام.',
      resolvedDescription: 'تم حل هذا الطلب تلقائياً عند اكتمال عملية الاستلام.',
      // مسار وثيقة الاستلام (بعد الموافقة، من Odoo)
      receivingJourney: 'مسار الاستلام',
      receivingJourneyDescription: 'تقدم شحنتك في المستودع في الوقت الفعلي.',
      receivingReference: 'مرجع الاستلام',
      currentStage: 'المرحلة الحالية',
      expectedArrival: 'الوصول المتوقع',
      arrivedAt: 'تاريخ الوصول',
      activityTimeline: 'الجدول الزمني للنشاط',
      workflowTimeline: 'الجدول الزمني للحالة',
      workflowTimelineDescription: 'تتبع شحنتك من المسودة حتى الإدخال إلى المخزون.',
      upNext: 'التالي',
      pending: 'قيد الانتظار',
      createdReceiving: 'تم إنشاء وثيقة الاستلام',
      stateChange: 'تغيرت الحالة',
      stateWfa: 'في انتظار الوصول',
      stateArrived: 'وصلت الشحنة',
      stateCount: 'العد',
      stateQa: 'فحص الجودة',
      stateDispute: 'نزاع',
      stateOnHold: 'موقوف',
      stateConfirm: 'مؤكد',
      stateEntryDone: 'تم الإدخال إلى المخزون',
      stateDraftReceiving: 'مسودة الاستلام',
      stateWfaDescription: 'تم تسجيل شحنتك ونحن بانتظار وصولها.',
      stateArrivedDescription: 'وصلت الشحنة إلى المستودع.',
      stateCountDescription: 'فريقنا يقوم بعد الأصناف المستلمة.',
      stateQaDescription: 'الأصناف قيد فحص الجودة.',
      stateConfirmDescription: 'تم تأكيد الاستلام؛ جارٍ تجهيز إدخال المخزون.',
      stateEntryDoneDescription: 'تمت إضافة الأصناف إلى مخزونك.',
      stateDisputeDescription: 'تم تسجيل اختلاف. سنتواصل معك لحل الأمر.',
      stateOnHoldDescription: 'الاستلام متوقف بانتظار حل النزاع.',
      noReceivingYet: 'ستظهر وثيقة الاستلام هنا بمجرد الموافقة على الإشعار.',
      by: 'بواسطة',
      // Dispatch
      dispatch: 'طلبات الإرسال (GRN)',
      createDispatch: 'إنشاء طلب إرسال',
      newDispatch: 'طلب إرسال جديد',
      dispatchReference: 'المرجع',
      requestedDate: 'التاريخ المطلوب',
      dispatchedDate: 'تاريخ الإرسال',
      deliveryAddress: 'عنوان التسليم',
      priority: 'الأولوية',
      normal: 'عادي',
      urgent: 'عاجل',
      requestedQty: 'الكمية المطلوبة',
      dispatchedQty: 'الكمية المرسلة',
      dispatchDetails: 'تفاصيل طلب الإرسال',
      noDispatch: 'لا توجد طلبات إرسال',
      pendingDispatch: 'طلبات إرسال معلقة',
      // Status workflow
      status: 'الحالة',
      draft: 'مسودة',
      review: 'قيد المراجعة',
      approved: 'موافق عليه',
      resolved: 'تم الحل',
      readyToReceive: 'جاهز للاستلام',
      readyToDispatch: 'جاهز للإرسال',
      inProcess: 'قيد التنفيذ',
      done: 'مكتمل',
      submitForReview: 'إرسال للمراجعة',
      approve: 'موافقة',
      reject: 'رفض',
      markReady: 'تحديد كجاهز',
      startProcess: 'بدء التنفيذ',
      complete: 'إكمال',
      setToDraft: 'تحويل إلى مسودة',
      cancelled: 'ملغي',
      cancelledDescription: 'تم رفض هذا الطلب.',
      readonlyMode: 'للقراءة فقط',
      // Form
      selectProducts: 'اختيار المنتجات',
      selectProductPlaceholder: 'اختر منتجًا...',
      attachments: 'المرفقات',
      createdBy: 'أنشأه',
      approvedBy: 'وافق عليه',
      createdAt: 'تاريخ الإنشاء',
      enterSupplierName: 'أدخل اسم المورد',
      enterNotes: 'أدخل الملاحظات...',
      productsAdded: 'منتجات مضافة',
      addProductsToAsn: 'أضف المنتجات التي تتوقع استلامها',
      summary: 'الملخص',
      asnInfoDescription: 'يساعد إشعار الشحن المسبق في تتبع المخزون الوارد قبل وصوله.',
      enterDeliveryAddress: 'أدخل عنوان التسليم...',
      addProductsToDispatch: 'أضف المنتجات التي تريد إرسالها',
      dispatchInfo: 'معلومات طلب الإرسال',
      dispatchInfoDescription: 'تساعد طلبات الإرسال في إدارة وتتبع الشحنات الصادرة.',
      // Charts
      stockDistribution: 'توزيع المخزون',
      topProducts: 'أفضل المنتجات',
      categoryDistribution: 'توزيع الفئات',
      recentMovements: 'الحركات الأخيرة',
      inventoryValue: 'قيمة المخزون',
      // Filters
      filterByStatus: 'تصفية حسب الحالة',
      filterByCategory: 'تصفية حسب الفئة',
      allStatuses: 'جميع الحالات',
      allCategories: 'جميع الفئات',
      all: 'الكل',
      outOfStock: 'نفاد المخزون',
      // Overview page
      overviewDescription: 'نظرة شاملة على مخزون المستودع الخاص بك',
      hiddenUntrackedStock: 'بعض العناصر غير معروضة لأنها غير متتبعة بالدفعة/الشحنة. يرجى التواصل معنا لتسويتها.',
      productsSKUs: 'المنتجات / SKUs',
      totalInventoryValue: 'إجمالي قيمة المخزون',
      categories: 'الفئات',
      totalSKUs: 'إجمالي المنتجات',
      // Overview sections
      operationsTitle: 'العمليات',
      operationsDescription: 'تتبع المخزون الوارد والصادر',
      masterDataTitle: 'البيانات الأساسية',
      masterDataDescription: 'المنتجات والإعدادات',
      // صفحة تفويضات النزاعات
      disputeMandatesTitle: 'تفويضات النزاعات',
      disputeMandatesDescription: 'قواعد مسبقة تحدد طريقة التسوية الآلية لنزاعات الاستلام',
      mandatesEmpty: 'لا توجد تفويضات بعد. أنشئ تفويضاً لتحديد كيفية تسوية نزاعات الاستلام تلقائياً.',
      createMandate: 'تفويض جديد',
      editMandate: 'تعديل التفويض',
      mandateReference: 'المرجع',
      mandateContract: 'العقد / اتفاقية الخدمة',
      mandateNoContract: 'بدون عقد محدد',
      mandateEffective: 'تاريخ السريان',
      mandateExpiry: 'تاريخ الانتهاء',
      mandateExpiryHint: 'اتركه فارغاً لعدم وجود انتهاء',
      mandateValidity: 'الصلاحية',
      mandateValid: 'ساري',
      mandateInvalid: 'غير ساري',
      mandateSignatory: 'المفوض بالتوقيع',
      mandateSignatoryTitle: 'مسمى المفوض',
      mandateSignatoryEmail: 'بريد المفوض',
      mandateSlaHours: 'مهلة رد العميل (ساعات)',
      mandateDefaultAction: 'الإجراء الافتراضي (عند عدم الرد)',
      mandateShipmentThreshold: 'حد قيمة الشحنة',
      mandateSkuThreshold: 'حد قيمة الصنف',
      mandateThresholdHelp: '0 = بدون حد',
      mandateNotes: 'ملاحظات',
      mandateRules: 'قواعد التفويض',
      mandateRulesEmpty: 'لا توجد قواعد بعد. استخدم "إضافة القواعد الافتراضية" لإنشاء كل أنواع النزاعات، أو أضف القواعد يدوياً.',
      mandateAddRule: 'إضافة قاعدة',
      mandateAddDefaultRules: 'إضافة القواعد الافتراضية',
      mandateRuleDisputeType: 'نوع النزاع',
      mandateRuleGranted: 'مفوض',
      mandateRuleThresholdType: 'نوع الحد',
      mandateRuleThresholdValue: 'قيمة الحد',
      mandateRuleAction: 'الإجراء المسموح',
      mandateAuthorizationSection: 'التفويض',
      mandateValiditySection: 'الصلاحية',
      mandateThresholdSection: 'الحدود واتفاقية الخدمة',
      mandateBackToList: 'العودة إلى التفويضات',
      mandateSubmitForApproval: 'إرسال للموافقة',
      mandateCancel: 'إلغاء التفويض',
      mandateResetToDraft: 'إعادة إلى المسودة',
      mandateSaved: 'تم حفظ التفويض',
      mandateCreated: 'تم إنشاء التفويض',
      mandateSaveFailed: 'فشل في حفظ التفويض',
      mandateLoadFailed: 'فشل في تحميل التفويض',
      mandateSubmittedForApproval: 'تم إرسال التفويض للموافقة',
      mandateCancelled: 'تم إلغاء التفويض',
      mandateResetDone: 'تمت إعادة التفويض إلى المسودة',
      mandateConfirmSubmit: 'إرسال هذا التفويض للموافقة؟ لن تتمكن من تعديل القواعد حتى الموافقة أو إعادة التعيين.',
      mandateConfirmCancel: 'إلغاء هذا التفويض؟ لا يمكن حذف التفويضات المعتمدة.',
      mandateStateDraft: 'مسودة',
      mandateStatePendingApproval: 'بانتظار الموافقة',
      mandateStateApproved: 'معتمد',
      mandateStateExpired: 'منتهي الصلاحية',
      mandateStateCancelled: 'ملغى',
      mandateRequiredSignatory: 'المفوض بالتوقيع مطلوب',
      mandateRequiredEffective: 'تاريخ السريان مطلوب',
      mandateRuleAtLeastOne: 'أضف قاعدة واحدة على الأقل قبل الإرسال',
      // صفحة قوالب الجودة
      qaTemplatesTitle: 'قوالب فحص الجودة',
      qaTemplatesDescription: 'قوالب فحص الجودة المطبقة عند استلام الشحنات',
      qaTemplatesEmpty: 'لا توجد قوالب بعد. أنشئ قالباً لتحديد فحوصات الجودة على استلامك.',
      createQaTemplate: 'قالب جديد',
      editQaTemplate: 'تعديل القالب',
      qaTemplateName: 'اسم القالب',
      qaTemplateSequence: 'الترتيب',
      qaTemplateActive: 'نشط',
      qaTemplateScope: 'النطاق',
      qaTemplateGlobalDefault: 'افتراضي عام',
      qaTemplateGlobal: 'عام',
      qaTemplateCategory: 'فئة المنتج',
      qaTemplateNoCategory: 'كل الفئات',
      qaTemplateNotes: 'ملاحظات',
      qaTemplateChecks: 'الفحوصات',
      qaTemplateChecksEmpty: 'لا توجد فحوصات. اضغط "إضافة فحص" لإضافة خطوة فحص.',
      qaTemplateAddCheck: 'إضافة فحص',
      qaTemplateCheckType: 'نوع الفحص',
      qaTemplateSamplingMethod: 'طريقة العينات',
      qaTemplateSampleSize: 'حجم العينة',
      qaTemplateMandatory: 'إلزامي',
      qaTemplateInstructions: 'تعليمات',
      qaTemplateBackToList: 'العودة إلى القوالب',
      qaTemplateOwnedByYou: 'مملوك لك',
      qaTemplateReadOnly: 'للقراءة فقط (قالب عام — انسخ للتعديل)',
      qaTemplateCloneToCustomise: 'نسخ للتعديل',
      qaTemplateSaved: 'تم حفظ القالب',
      qaTemplateCreated: 'تم إنشاء القالب',
      qaTemplateDeleted: 'تم حذف القالب',
      qaTemplateSaveFailed: 'فشل في حفظ القالب',
      qaTemplateLoadFailed: 'فشل في تحميل القالب',
      qaTemplateDeleteFailed: 'فشل في حذف القالب',
      qaTemplateConfirmDelete: 'حذف هذا القالب؟ لا يمكن التراجع.',
      qaTemplateRequiredName: 'اسم القالب مطلوب',
      qaTemplateRequiredCheck: 'أضف فحصاً واحداً على الأقل',
      // صفحة تفاصيل النزاع
      disputeBackToList: 'العودة إلى النزاعات',
      disputeDetailTitle: 'النزاع',
      disputeLoadFailed: 'فشل في تحميل النزاع',
      disputeApprove: 'الموافقة على التسوية',
      disputeReject: 'رفض التسوية',
      disputeClientApprove: 'موافقة',
      disputeClientReject: 'رفض',
      disputeApproveTitle: 'الموافقة على التسوية',
      disputeApproveMessage: 'أكد الإجراء الذي سيقوم به المستودع لتسوية هذا النزاع.',
      disputeRejectTitle: 'رفض التسوية',
      disputeRejectMessage: 'وضّح للمستودع سبب الرفض. سيتم تحويل النزاع إلى حالة "مرفوض".',
      disputeApproved: 'تمت الموافقة على التسوية',
      disputeRejected: 'تم رفض التسوية',
      disputeActionFailed: 'فشل الإجراء',
      disputeResolution: 'التسوية',
      disputeSuggestedAction: 'الإجراء المقترح',
      disputeResolutionAction: 'إجراء التسوية',
      disputeClientNotes: 'ملاحظاتك (اختياري)',
      disputeMandateUsed: 'التفويض المطبق',
      disputeMandateEvaluation: 'تقييم التفويض',
      disputeAttachments: 'الأدلة',
      disputeNoAttachments: 'لا توجد مرفقات',
      disputeDescription: 'الوصف',
      disputeQuantities: 'الكميات',
      disputeAsnQty: 'الكمية المتوقعة',
      disputeActualQty: 'الكمية الفعلية',
      disputeVarianceQty: 'الفرق',
      disputeUnitValue: 'سعر الوحدة',
      disputeTotalValue: 'القيمة المتنازع عليها',
      disputeWarehouseResolved: 'قام المستودع بتسوية هذا النزاع',
      disputeClosedInfo: 'النزاع مغلق ومقفل',
      disputeAwaitingClient: 'بانتظار قرارك',
      disputeNoActionsAvailable: 'لا توجد إجراءات متاحة — تم تسوية النزاع بالفعل.',
      comingSoon: 'قريباً',
      comingSoonDescription: 'ستكون هذه الصفحة متاحة قريباً. للوصول المبكر تواصل مع مدير حسابك.',
      // صفحة النزاعات
      disputesTitle: 'النزاعات',
      disputesDescription: 'نزاعات الاستلام الواردة المسجلة على شحناتك',
      disputesEmpty: 'لا توجد نزاعات تطابق التصفية الحالية',
      disputesFilterAll: 'الكل',
      disputesFilterSlaBreach: 'تجاوز SLA',
      disputesFilterSlaBreachActive: 'تجاوز SLA فقط',
      disputeReference: 'المرجع',
      disputeType: 'النوع',
      disputeSeverity: 'الخطورة',
      disputeStatus: 'الحالة',
      disputeProduct: 'المنتج',
      disputeAsn: 'إشعار الشحن',
      disputeReceiving: 'الاستلام',
      disputeVariance: 'الفرق',
      disputeValue: 'القيمة المتنازع عليها',
      disputeSla: 'SLA',
      disputeSlaDeadline: 'الموعد النهائي SLA',
      disputeCreated: 'تاريخ الإنشاء',
      disputeNotified: 'تاريخ الإشعار',
      disputeResponded: 'تاريخ الرد',
      // حالات النزاع
      disputeStateDraft: 'مسودة',
      disputeStateOpen: 'مفتوح',
      disputeStateWaitingClient: 'بانتظار العميل',
      disputeStateAutoResolved: 'تمت التسوية تلقائياً',
      disputeStateClientApproved: 'موافقة العميل',
      disputeStateRejected: 'مرفوض',
      disputeStateClosed: 'مغلق',
      // مستويات الخطورة
      disputeSeverityMinor: 'بسيطة',
      disputeSeverityMajor: 'كبيرة',
      disputeSeverityCritical: 'حرجة',
      // مؤشرات SLA
      slaOnTrack: 'في الوقت',
      slaWarning: 'تحذير',
      slaBreached: 'متجاوز',
      slaNA: 'غير متاح',
      // Status labels (for filters/badges)
      statusInStock: 'في المخزون',
      statusOutOfStock: 'نفاد المخزون',
      statusLowStock: 'مخزون منخفض',
      // Image Upload
      clickToUpload: 'انقر للرفع',
      dragAndDrop: 'اسحب وأفلت',
      or: 'أو',
      upTo: 'حتى',
      uploading: 'جاري الرفع...',
      changeImage: 'تغيير الصورة',
      removeImage: 'إزالة الصورة',
      invalidImageFormat: 'يرجى اختيار ملف صورة صالح (PNG, JPG, GIF)',
      imageTooLarge: 'يجب أن يكون حجم الصورة أقل من 5 ميجابايت',
      uploadFailed: 'فشل في رفع الصورة',
      productImage: 'صورة المنتج',
      // Supplier Combobox
      noMatchingSuppliers: 'لا يوجد موردين مطابقين',
      addNewSupplier: 'إضافة مورد جديد',
      addAsNewSupplier: 'إضافة "{supplier}" كمورد جديد',
      // Table headers
      actions: 'الإجراءات',
      expiry: 'تاريخ الانتهاء',
      // Summary metrics
      totalSuppliers: 'إجمالي الموردين',
      totalCategories: 'إجمالي الفئات',
      totalUnits: 'إجمالي الوحدات',
      totalQuantityOnHand: 'إجمالي الكمية المتوفرة',
      qtyOnHand: 'الكمية المتوفرة',
      viewProducts: 'عرض المنتجات',
      filteringBy: 'التصفية حسب',
      clearContext: 'مسح التصفية',
      // Product Tabs
      warehousesLocations: 'المستودعات والمواقع',
      suppliers: 'الموردين',
      batches: 'الدفعات / اللوتات',
      // Management Pages
      manageSuppliers: 'إدارة الموردين',
      categoriesTitle: 'الفئات',
      manageCategories: 'إدارة الفئات',
      unitsTitle: 'الوحدات',
      manageUnits: 'إدارة الوحدات',
      createSupplier: 'إنشاء مورد',
      newSupplier: 'مورد جديد',
      editSupplier: 'تعديل المورد',
      supplierDetails: 'تفاصيل المورد',
      noSuppliersFound: 'لم يتم العثور على موردين',
      productsSupplied: 'المنتجات الموردة',
      noProductsSupplied: 'لا توجد منتجات موردة من هذا المورد حتى الآن',
      viewAllProducts: 'عرض جميع المنتجات',
      viewInProductList: 'عرض في قائمة المنتجات',
      totalQuantityProvided: 'إجمالي الكمية الموردة',
      qtyProvided: 'الكمية الموردة',
      preferred: 'مفضل',
      lastOrder: 'آخر طلب',
      days: 'أيام',
      view: 'عرض',
      totalProducts: 'إجمالي المنتجات',
      totalQuantity: 'إجمالي الكمية',
      product: 'المنتج',
      availableQuantity: 'الكمية المتاحة',
      reservedQuantity: 'الكمية المحجوزة',
      suppliersOfThisProduct: 'الموردون الذين يوفرون هذا المنتج',
      addFirstSupplier: 'إضافة أول مورد',
      viewSupplier: 'عرض المورد',
      quickActions: 'إجراءات سريعة',
      createCategory: 'إنشاء فئة',
      newCategory: 'فئة جديدة',
      editCategory: 'تعديل الفئة',
      categoryDetails: 'تفاصيل الفئة',
      categoryName: 'اسم الفئة',
      noCategoriesFound: 'لم يتم العثور على فئات',
      createUnit: 'إنشاء وحدة',
      newUnit: 'وحدة جديدة',
      editUnit: 'تعديل الوحدة',
      unitDetails: 'تفاصيل الوحدة',
      noUnitsFound: 'لم يتم العثور على وحدات',
      contactInfo: 'معلومات الاتصال',
      address: 'العنوان',
      website: 'الموقع الإلكتروني',
      activeProducts: 'المنتجات النشطة',
      totalOrders: 'إجمالي الطلبات',
      productsCount: 'المنتجات',
      parentCategory: 'الفئة الأصل',
      noParentCategory: 'لا يوجد (فئة جذر)',
      active: 'نشط',
      inactive: 'غير نشط',
      searchSuppliers: 'البحث عن الموردين...',
      searchCategories: 'البحث عن الفئات...',
      searchUnits: 'البحث عن الوحدات...',
      code: 'الرمز',
      units: 'وحدات',
      conversionSettings: 'إعدادات التحويل',
      // Warehouse & Location
      warehouse: 'المستودع',
      selectWarehouse: 'اختر المستودع',
      locationInWarehouse: 'الموقع',
      selectLocation: 'اختر الموقع',
      addWarehouseLocation: 'إضافة موقع مستودع',
      noWarehouseLocations: 'لا توجد مواقع مستودعات مضافة',
      removeLocation: 'إزالة الموقع',
      // Suppliers (additional fields)
      selectSupplier: 'اختر المورد',
      supplierCode: 'رمز المورد',
      contactPerson: 'جهة الاتصال',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      leadTime: 'وقت التسليم (أيام)',
      minOrderQty: 'الحد الأدنى للطلب',
      price: 'السعر',
      currency: 'العملة',
      isPreferred: 'مورد مفضل',
      lastPurchaseDate: 'آخر شراء',
      addSupplier: 'إضافة مورد',
      noSuppliers: 'لا يوجد موردين مضافين',
      noClientsSuppliers: 'لا يوجد موردين مخصصين لهذا المنتج',
      removeSupplier: 'إزالة المورد',
      assignedSuppliers: 'الموردون المعينون',
      supplierAdded: 'تمت إضافة المورد بنجاح',
      supplierRemoved: 'تمت إزالة المورد بنجاح',
      errorAddingSupplier: 'فشل في إضافة المورد',
      errorRemovingSupplier: 'فشل في إزالة المورد',
      vendorPricelist: 'قائمة أسعار الموردين',
      // Products in Supplier
      assignedProducts: 'المنتجات المعينة',
      noProductsAssigned: 'لا توجد منتجات مخصصة لهذا المورد',
      productAdded: 'تمت إضافة المنتج بنجاح',
      productRemoved: 'تمت إزالة المنتج بنجاح',
      errorAddingProduct: 'فشل في إضافة المنتج',
      errorRemovingProduct: 'فشل في إزالة المنتج',
      add: 'إضافة',
      // Batches / Lots
      batchNumber: 'رقم الدفعة',
      lotNumber: 'رقم اللوت',
      manufacturingDate: 'تاريخ التصنيع',
      batchStatus: 'حالة الدفعة',
      available: 'متاح',
      reserved: 'محجوز',
      quarantine: 'حجر صحي',
      nearExpiry: 'قارب الانتهاء',
      expiryUnknown: 'غير معروف',
      addBatch: 'إضافة دفعة',
      noBatches: 'لا توجد دفعات مضافة',
      removeBatch: 'إزالة الدفعة',
      // Unit of Measure
      selectOrTypeUnit: 'اختر أو اكتب الوحدة...',
      noMatchingUnits: 'لا توجد وحدات مطابقة',
      createNewUnit: 'إنشاء وحدة جديدة',
      unitCategory: 'فئة الوحدة',
      unitName: 'اسم الوحدة',
      unitSymbol: 'الرمز',
      parentUnit: 'الوحدة الأساسية',
      noParentUnit: 'لا يوجد (وحدة أساسية)',
      parentUnitHelp: 'اختر وحدة أساسية لتحديد علاقة التحويل',
      conversionRatio: 'نسبة التحويل',
      thisUnit: 'هذه الوحدة',
      conversionExample: '{count} {unit} يساوي 1 {parent}',
      // Threshold modal
      thresholdAlertMessage: 'ستتلقى تنبيهات عندما ينخفض المخزون دون هذا الحد.',
      // Common actions (use common.cancel, common.close, etc. instead)
      saveChanges: 'حفظ التغييرات',
      openFullPage: 'فتح الصفحة الكاملة',
      expand: 'توسيع',
      close: 'إغلاق',
      noProducts: 'لا توجد منتجات',
      completed: 'مكتمل',
    },

    // Profile
    profile: {
      title: 'الملف الشخصي',
      personalInfo: 'المعلومات الشخصية',
      companyInfo: 'معلومات الشركة',
      addresses: 'العناوين',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      companyName: 'اسم الشركة',
      vatNumber: 'الرقم الضريبي',
      profileImage: 'صورة الملف الشخصي',
      changeImage: 'تغيير الصورة',
      addAddress: 'إضافة عنوان',
      addressType: 'نوع العنوان',
      delivery: 'توصيل',
      office: 'مكتب',
      setDefault: 'تعيين كافتراضي',
      defaultAddress: 'العنوان الافتراضي',
      addressText: 'العنوان',
      editAddress: 'تعديل العنوان',
      deleteAddress: 'حذف العنوان',
      saveChanges: 'حفظ التغييرات',
      savedSuccess: 'تم حفظ الملف الشخصي بنجاح!',
      saveError: 'فشل في حفظ الملف الشخصي',
      addressUpdated: 'تم تحديث العنوان بنجاح!',
      addressAdded: 'تم إضافة العنوان بنجاح!',
      addressError: 'فشل في حفظ العنوان',
    },

    // Correspondence
    correspondence: {
      title: 'المراسلات',
      emailNotifications: 'إشعارات البريد الإلكتروني',
      subject: 'الموضوع',
      date: 'التاريخ',
      from: 'من',
      to: 'إلى',
      preview: 'معاينة',
      viewDetails: 'عرض التفاصيل',
      noMessages: 'لا توجد رسائل',
      compose: 'إنشاء',
      composeNew: 'رسالة جديدة',
      body: 'الرسالة',
      attachments: 'المرفقات',
      addAttachment: 'إضافة مرفق',
      removeAttachment: 'إزالة',
      send: 'إرسال',
      sending: 'جاري الإرسال...',
      sent: 'مُرسل',
      received: 'مُستلم',
      messageSent: 'تم إرسال الرسالة بنجاح',
      unread: 'غير مقروء',
      unreadMessages: 'الرسائل غير المقروءة',
      showingPerPage: 'عرض {count} بريد لكل صفحة',
      filterAll: 'الكل',
      filterUnread: 'غير مقروء',
      filterWithAttachments: 'مع مرفقات',
      filterToday: 'اليوم',
      filterThisWeek: 'هذا الأسبوع',
      filterThisMonth: 'هذا الشهر',
      filterCustomRange: 'نطاق مخصص',
      fromDate: 'من تاريخ',
      toDate: 'إلى تاريخ',
      applyFilter: 'تطبيق',
      clearFilter: 'مسح',
      downloadAttachment: 'تحميل',
      attachmentSize: 'الحجم',
    },

    // Theme & Language
    settings: {
      theme: 'المظهر',
      language: 'اللغة',
      darkMode: 'الوضع الداكن',
      lightMode: 'الوضع الفاتح',
      english: 'English',
      arabic: 'العربية',
    },
  },
} as const;

export type TranslationKeys = typeof translations.en;

